
import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users, ChevronRight, Star } from 'lucide-react';
import { Candidate } from '@/components/CandidateCard';

interface CandidatesListProps {
  candidates: Candidate[];
  currentCandidateId: string;
  showCandidateList: boolean;
  toggleCandidateList: () => void;
}

const CandidatesList = ({ 
  candidates, 
  currentCandidateId, 
  showCandidateList, 
  toggleCandidateList 
}: CandidatesListProps) => {
  const getStatusBadge = (status: Candidate['status']) => {
    switch (status) {
      case 'invited': return <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200">Invited</Badge>;
      case 'in_progress': return <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-200">In Progress</Badge>;
      case 'completed': return <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">Completed</Badge>;
    }
  };

  return (
    <div className={`border-r bg-muted/10 ${showCandidateList ? 'w-72' : 'w-0'} transition-all duration-300 overflow-hidden`}>
      <div className="p-4 border-b sticky top-0 bg-background/95 backdrop-blur-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users size={18} />
            <h2 className="font-medium">Candidates</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={toggleCandidateList}>
            <ChevronRight size={18} />
          </Button>
        </div>
      </div>
      
      <div className="overflow-y-auto h-[calc(100vh-60px)]">
        {candidates.map((candidate) => (
          <div
            key={candidate.id}
            className={`block p-3 hover:bg-muted/50 border-b cursor-pointer ${candidate.id === currentCandidateId ? 'bg-muted/50' : ''}`}
          >
            <div className="flex items-center space-x-3">
              <Avatar className="h-8 w-8 border border-border">
                <AvatarImage src={candidate.avatarUrl} alt={candidate.name} />
                <AvatarFallback className="bg-roundone-purple/10 text-roundone-purple text-xs">
                  {candidate.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="space-y-1 flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{candidate.name}</div>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-muted-foreground truncate">{candidate.email}</div>
                  {candidate.status === 'completed' && candidate.aiRating && (
                    <div className="flex items-center">
                      <Star size={12} className="text-yellow-400 fill-yellow-400" />
                      <span className="ml-1 text-xs">{candidate.aiRating.toFixed(1)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-1 flex justify-between items-center">
              <span className="text-xs text-muted-foreground">{candidate.assessmentTitle}</span>
              {getStatusBadge(candidate.status)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CandidatesList;
